import {
  take,
  put,
  select,
  takeLatest,
  cancel,
  call,
} from 'redux-saga/effects';

import { SCHEDULE_VALIDATE_TYPES, scheduleValidateActions } from '../validate';
import {
  selectSchedulePopoverData,
  selectScheduleTargerProviderId,
} from '../selectors';
import {
  RECURSION_MODAL_TYPES,
  RECURSION_OPTIONS,
} from '../../../components/ProviderCalendar/constants';
import {
  getPrevDayRepeatableAdapter,
  getRepeatFlow,
  isFirstDayOfCustomRepeatChange,
  isLastDayOfRepeat,
  isOnlyEndRepeatDateChange,
  noRepeatScheduleAdapter,
  removeRepeatScheduleAdapter,
  scheduleCurrentDaySettingsAttrsAdapter,
  scheduleFormAdapter,
  scheduleOriginDaySettingsAttrsAdapter,
} from '../../../utils/schedule/repeat-schedule-adapter';
import * as workslotUpdate from '../requests/workslot-update-req';
import * as workslotRemove from '../requests/workslot-remove-req';
import * as workslotInsert from '../requests/workslot-insert-req';
import { findIntersections } from './find-intersections-saga';

function* displayRecursionModal(recursionFlow) {
  const selectedSchedule = yield select(selectSchedulePopoverData);

  yield put(
    scheduleValidateActions.toggleRecursionModal({
      type: RECURSION_MODAL_TYPES.EDIT_WORK_SLOT,
      lastDay: isLastDayOfRepeat(selectedSchedule),
      opened: true,
      recursionFlow,
    }),
  );

  const {
    payload: { option: recursionOption },
  } = yield take(SCHEDULE_VALIDATE_TYPES.APPLY_RECURSION);

  return recursionOption;
}

/**
 *      CHANGE WORK SLOT REPEAT HANDLERS
 * */
function* proceedNonRepeatableChange({ selectedSchedule, repeatData, id }) {
  const scheduleFormData = scheduleFormAdapter(selectedSchedule);
  const attrs = scheduleOriginDaySettingsAttrsAdapter(selectedSchedule);
  const updatePayload = {
    slotId: selectedSchedule.id,
    workSlot: {
      ...scheduleFormData,
      scheduleSettingAttributes: {
        ...attrs,
        ...repeatData,
        endOn: repeatData.endOn || null,
      },
    },
    id,
  };
  const canInsert = yield call(findIntersections, updatePayload);
  if (canInsert) {
    yield put(workslotUpdate.actions.send(updatePayload));
  }
}

function* proceedCustomRepeatWithFollowingOption({
  selectedSchedule,
  repeatData,
  id,
}) {
  const scheduleFormData = scheduleFormAdapter(selectedSchedule);
  const attrs = scheduleCurrentDaySettingsAttrsAdapter(selectedSchedule);

  const updatePayload = {
    slotId: selectedSchedule.id,
    workSlot: {
      ...scheduleFormData,
      ...getPrevDayRepeatableAdapter(selectedSchedule),
    },
    id,
  };
  const insertPayload = {
    id,
    workSlot: {
      ...scheduleFormData,
      scheduleSettingAttributes: { ...attrs, ...repeatData },
    },
  };

  const canInsert = yield call(findIntersections, insertPayload);
  if (canInsert) {
    yield put(workslotUpdate.actions.send(updatePayload));
    yield take(workslotUpdate.actions.succeed);
    yield put(workslotInsert.actions.send(insertPayload));
  }
}

function* proceedCustomRepeatWithAllOption({
  selectedSchedule,
  repeatData,
  id,
}) {
  const scheduleFormData = scheduleFormAdapter(selectedSchedule);
  const attrs = scheduleOriginDaySettingsAttrsAdapter(selectedSchedule);
  const updatePayload = {
    slotId: selectedSchedule.id,
    workSlot: {
      ...scheduleFormData,
      scheduleSettingAttributes: { ...attrs, ...repeatData },
    },
    id,
  };

  const canInsert = yield call(findIntersections, updatePayload);
  if (canInsert) {
    yield put(workslotUpdate.actions.send(updatePayload));
  }
}

function* proceedNoRepeatWithFollowingOption({ selectedSchedule, id }) {
  const removedSlot = noRepeatScheduleAdapter(selectedSchedule);

  yield put(workslotRemove.actions.send({ id, removedSlot }));
}

function* proceedNoRepeatWithAllOption({ selectedSchedule, id }) {
  const removedSlot = removeRepeatScheduleAdapter(
    RECURSION_OPTIONS.ALL_DAYS,
    selectedSchedule,
  );
  const scheduleSettingAttributes = removeRepeatScheduleAdapter(
    RECURSION_OPTIONS.CURRENT_DAY,
    selectedSchedule,
  );
  const scheduleFormData = scheduleFormAdapter(selectedSchedule);
  const workSlot = { ...scheduleFormData, ...scheduleSettingAttributes };

  yield put(workslotRemove.actions.send({ id, removedSlot }));
  yield take(workslotRemove.actions.succeed);
  yield put(workslotInsert.actions.send({ id, workSlot }));
}

/**
 *      ROOT RECURSION HANDLER
 * */
function* handleEditWorkRecursion({ payload }) {
  const id = yield select(selectScheduleTargerProviderId);
  const selectedSchedule = yield select(selectSchedulePopoverData);
  const { repeatData, repeatValue } = payload;
  const recursionFlow = getRepeatFlow(repeatValue);
  const shared = { id, selectedSchedule, repeatData };

  if (!selectedSchedule.isRepeatable) {
    yield call(proceedNonRepeatableChange, shared);
    yield cancel();
  }

  if (isOnlyEndRepeatDateChange(selectedSchedule, payload)) {
    yield call(proceedCustomRepeatWithAllOption, shared);
    yield cancel();
  }

  if (isFirstDayOfCustomRepeatChange(selectedSchedule, recursionFlow)) {
    yield call(proceedCustomRepeatWithAllOption, shared);
    yield cancel();
  }

  const recursionOption = yield call(displayRecursionModal, recursionFlow);

  switch (recursionOption) {
    case RECURSION_OPTIONS.UPDATE_CUSTOM_FOLOWING: {
      yield call(proceedCustomRepeatWithFollowingOption, shared);
      yield cancel();
      break;
    }

    case RECURSION_OPTIONS.UPDATE_CUSTOM_ALL: {
      yield call(proceedCustomRepeatWithAllOption, shared);
      yield cancel();
      break;
    }

    case RECURSION_OPTIONS.NO_REPEAT_FOLOWING: {
      yield call(proceedNoRepeatWithFollowingOption, shared);
      yield cancel();
      break;
    }

    case RECURSION_OPTIONS.NO_REPEAT_ALL: {
      yield call(proceedNoRepeatWithAllOption, shared);
      yield cancel();
      break;
    }

    default:
      console.log('unhandled recursion option', recursionOption, recursionFlow);
      yield cancel();
  }
}

export default function* editWorkRecursionSaga() {
  yield takeLatest(
    [SCHEDULE_VALIDATE_TYPES.EDIT_WORK_RECURSION],
    handleEditWorkRecursion,
  );
}



// WEBPACK FOOTER //
// ./src/redux/schedule/sagas/edit-work-recursion-saga.js